import { call, put, takeLatest, select } from 'redux-saga/effects';
import { t } from 'i18next';

import { Api } from '../../../entries/ApiTransport';
import sagaAssessor from '../../../utils/sagaAssessor';
import { authActions, authTypes } from './actions';
import { setActiveSalonInfo } from '../../../utils/salonToken';
import {
  getActiveSalonId,
  getSalonCity,
  getSalons,
  getSalonsRaw,
  isHomeStatsHidden,
  readSalonCityFields,
  salonWorkTimes,
} from './selectors';
import { toastActions } from 'Containers/Toasts/store';
import { clearHomeStatCache } from 'utils/cacheManager';
import { syncHasShopFromSalon } from 'utils/hasShopAccess';
import { homeActions } from 'Containers/Home/store';
import { statisticActions } from 'Containers/Statistic/store';
import { settingsActions } from 'Containers/Settings/store';
import { sendMessageToApp } from 'utils/exsternalCall';
import { clearSessionStoragePreservingFromApp } from 'utils/clearSessionStorage';
import { markSalonsListMigrationComplete } from 'utils/businessToken';
import { bumpIconCache, imageEntities } from 'utils/ImagesCreator';
import { rememberSalonCities, rememberSalonCity } from 'utils/salonCityCache';
import { clearAllStatsUnlock, clearStatsUnlock } from 'utils/statsUnlock';
import { isDemoMode, resetDemoSession } from 'demo';

const api = Api.getInstance();

const CITY_FIELDS = [
  'citta',
  'cittaSalone',
  'citta_salone',
  'comune',
  'comune_salone',
  'city',
  'localita',
  'nome_citta',
  'nomeCitta',
  'provincia',
  'provincia_salone',
  'provinciaSalone',
  'prov',
  'sigla_provincia',
  'siglaProvincia',
  'indirizzo',
];

/** tokenLogin restituisce solo il salone corrente: aggiorna la lista senza sostituirla. */
function mergeSalonFromTokenLogin(existingSalons, saloneFromApi, activeId) {
  const resolvedActiveId = activeId || saloneFromApi.idsalone;
  const list = existingSalons?.length ? existingSalons : [];
  const index = list.findIndex((salon) => +salon.idsalone === +saloneFromApi.idsalone);

  const mergeOne = (existing) => {
    const merged = {
      ...(existing || {}),
      ...saloneFromApi,
      isActive: +saloneFromApi.idsalone === +resolvedActiveId,
    };

    const apiCity = readSalonCityFields(saloneFromApi);
    const existingCity = getSalonCity(existing);
    // Se l'API non manda la città, non cancellare storage/cache
    if (!apiCity && existingCity) {
      CITY_FIELDS.forEach((field) => {
        if (existing?.[field] != null && existing[field] !== '') {
          merged[field] = existing[field];
        }
      });
      if (!merged.citta) merged.citta = existingCity;
    }

    const resolvedCity = readSalonCityFields(merged) || getSalonCity(merged);
    if (resolvedCity) {
      rememberSalonCity(merged.idsalone, resolvedCity);
      if (!merged.citta) merged.citta = resolvedCity;
    }

    return merged;
  };

  if (index !== -1) {
    return list.map((salon, salonIndex) =>
      salonIndex === index
        ? mergeOne(salon)
        : { ...salon, isActive: +salon.idsalone === +resolvedActiveId },
    );
  }
  if (list.length) {
    return list.map((salon) =>
      +salon.idsalone === +saloneFromApi.idsalone
        ? mergeOne(salon)
        : { ...salon, isActive: +salon.idsalone === +resolvedActiveId },
    );
  }
  return [{ ...mergeOne(null), isActive: true }];
}

const signIn = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { token } = payload;
        let link = '/business/auth/login';
        if (token) {
          link = '/business/auth/tokenLogin';
        }
        const { data } = yield call(() =>
          token
            ? api.post(link, payload, { headers: { Authorization: token } })
            : api.post(link, payload),
        );
        const salons = [];
        const salone = data?.salone;
        const loginSalons = salone?.saloni;
        if (+salone?.isSuperAdmin === 1 && Array.isArray(loginSalons)) {
          const activeId = salone.idsalone;
          const activeSalons = loginSalons
            .filter((item) => +item.eliminato === 0)
            .map((item, index) => ({
              ...item,
              isActive: activeId != null ? +item.idsalone === +activeId : !index,
            }));
          if (activeSalons.length && !activeSalons.some((s) => s.isActive)) {
            activeSalons[0].isActive = true;
          }
          salons.push(...activeSalons);
        } else if (salone?.token) {
          salons.push({
            ...salone,
            isActive: true,
          });
        }

        if (salons.length) {
          clearSessionStoragePreservingFromApp();
          clearAllStatsUnlock();
          resetDemoSession();
          rememberSalonCities(salons);
          localStorage.setItem('salons', JSON.stringify(salons));
          markSalonsListMigrationComplete();
          const activeSalonId = salons?.find((salon) => salon.isActive)?.idsalone;
          yield put(
            authActions.SIGN_IN.SUCCESS({
              activeSalonId,
              salons,
            }),
          );
        }

        if (data?.role === 1) {
          localStorage.setItem('staff', data.idUtenteBusiness);
        }

        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.SIGN_IN.FAILED(err),
  );

const fetchSalon = ({ _, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => api.post('/business/auth/tokenLogin'));
        const currentActiveSalonId = yield select(getActiveSalonId());
        const salonsRaw = localStorage.getItem('salons');
        const storedSalons = salonsRaw ? JSON.parse(salonsRaw) : [];
        const activeId = currentActiveSalonId || data.salone.idsalone;
        const activeSalons = mergeSalonFromTokenLogin(
          storedSalons,
          data.salone,
          activeId,
        );
        rememberSalonCities(activeSalons);
        localStorage.setItem('salons', JSON.stringify(activeSalons));

        const activeSalon = activeSalons.find((salon) => salon.isActive) || data.salone;
        syncHasShopFromSalon(activeSalon);

        const previousActive =
          storedSalons.find((salon) => +salon.idsalone === +activeId) ||
          storedSalons.find((salon) => salon?.isActive);
        const wasDemo = isDemoMode(previousActive);
        const nowDemo = isDemoMode(activeSalon);
        const demoFlagChanged = wasDemo !== nowDemo;

        if (demoFlagChanged) {
          resetDemoSession();
          clearHomeStatCache();
        }

        yield put(
          authActions.FETCH_SALON.SUCCESS({
            activeSalonId: activeSalon?.idsalone,
            salons: activeSalons,
          }),
        );
        if (demoFlagChanged) {
          yield put(statisticActions.STATISTICS_SESSION_RESET.SUCCESS());
        }
        if (nowDemo || demoFlagChanged) {
          if (!isHomeStatsHidden(activeSalon)) {
            yield put(homeActions.GET_HOME_STAT.REQUEST({}, null, { skipLoader: true }));
          }
          yield put(homeActions.GET_QUEUE.REQUEST({}, null, { skipLoader: true }));
        }
        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.FETCH_SALON.FAILED(err),
    options,
  );

const SKIP_LOADER = { skipLoader: true };

const setActiveSalon = ({ payload: salonId, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const salons = yield select(getSalonsRaw());
        const previousSalonId = yield select(getActiveSalonId());
        const targetSalon = salons.find((salon) => +salon.idsalone === +salonId);
        const hideStats = isHomeStatsHidden(targetSalon);

        if (previousSalonId != null && +previousSalonId !== +salonId) {
          clearStatsUnlock(previousSalonId);
          resetDemoSession();
        }

        yield put(authActions.SET_ACTIVE_SALON.SUCCESS(salonId));

        clearHomeStatCache();
        try {
          localStorage.removeItem('currentPrice');
        } catch (_) {
          /* ignore */
        }

        // Soft refresh: no global loader stacking; Home/Plan update without full-page block
        yield put(authActions.FETCH_SALON.REQUEST({}, null, SKIP_LOADER));
        yield put(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}, null, SKIP_LOADER));

        if (!hideStats) {
          yield put(homeActions.GET_HOME_STAT.REQUEST({}, null, SKIP_LOADER));
        }
        yield put(homeActions.GET_QUEUE.REQUEST({}, null, SKIP_LOADER));

        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.SET_ACTIVE_SALON.FAILED(err),
    SKIP_LOADER,
  );

const editSalon = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const {
          data: { salone },
        } = yield call(() => api.post('/business/saloni/modInfoSalone', payload));
        setActiveSalonInfo(salone);
        yield put(authActions.EDIT_SALON_INFO.SUCCESS(salone));
        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.EDIT_SALON_INFO.FAILED(err),
  );

const resetHomeStats = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const activeSalonId = yield select(getActiveSalonId());
        const {
          data: { salone },
        } = yield call(() => api.post('/business/saloni/resetHomeStats', payload));

        toastActions.OPEN_TOAST.SUCCESS({
          type: 'success',
          props: { title: 'Dati resettati con successo' },
        });

        const salonPayload = {
          ...(salone || {}),
          idsalone: salone?.idsalone ?? activeSalonId,
          hideHomeStats: payload?.hideHomeStats,
        };

        setActiveSalonInfo(salonPayload);
        yield put(authActions.RESET_HOME_STATS.SUCCESS(salonPayload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => {
      toastActions.OPEN_TOAST.ERROR({
        type: 'error',
        props: { title: 'Errore nel reset dei dati, riprova più tardi' },
      });
      authActions.RESET_HOME_STATS.FAILED(err);
    },
  );

const activePayment = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const {
          data: { salone },
        } = yield call(() => api.post('/business/saloni/attivaPagamenti', payload));
        setActiveSalonInfo(salone);
        yield put(authActions.ACTIVE_PAYMENT.SUCCESS(salone));
        callback && typeof callback === 'function' && callback();
        yield put(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'payment',
            props: { title: t('common.toastMessages.paymentsActivated') },
          }),
        );
      },
    (err) => authActions.ACTIVE_PAYMENT.FAILED(err),
  );

const editSalonAvatar = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        yield call(() => api.post('/img/setProfilo', payload));
        const salonId = yield select(getActiveSalonId());
        bumpIconCache(imageEntities.salon, salonId, 'profilo');
        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.EDIT_SALON_AVATAR.FAILED(err),
  );

const editSalonCover = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        yield call(() => api.post('/img/setCopertina', payload));
        const salonId = yield select(getActiveSalonId());
        bumpIconCache(imageEntities.salon, salonId, 'copertina');
        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.EDIT_SALON_COVER.FAILED(err),
  );

const editSalonPassword = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const {
          data: { salone },
        } = yield call(() => api.post('/business/auth/cambiaPwd', payload));
        setActiveSalonInfo({ idsalone: salone[0].idsalone, token: salone[0].token });
        yield put(authActions.FETCH_SALON.REQUEST());
        yield put(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'success',
            props: { title: t('security.toast.passwordUpdated') },
          }),
        );
        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.EDIT_SALON_PASSWORD.FAILED(err),
  );

const deleteSalon = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const fromApp = !!localStorage.getItem('fromApp');
        localStorage.clear();
        if (fromApp) localStorage.setItem('fromApp', 'true');
        yield call(() => api.post('/business/auth/deleteSalone', payload));

        const activeSalonId = yield select(getActiveSalonId());
        const salons = yield select(getSalons());
        const activeSalons = salons.filter((salon) => salon.value !== activeSalonId);

        if (!activeSalons.length) {
          localStorage.removeItem('salons');
          yield put(authActions.LOG_OUT.SUCCESS());
        } else {
          const [{ value }] = activeSalons;
          yield put(authActions.SET_ACTIVE_SALON.REQUEST(value));
          yield put(authActions.DELETE_SALON.SUCCESS(activeSalonId));
        }
        callback && typeof callback === 'function' && callback();
      },
    (err) => {
      authActions.DELETE_SALON.FAILED(err);
      if (err.response.status === 401) {
        localStorage.removeItem('salons');
        window.location.href = '/login';
      }
    },
  );

const logOut = ({ callback }) =>
  sagaAssessor(
    () =>
      function* () {
        sendMessageToApp('app:logout');
        clearSessionStoragePreservingFromApp();
        clearAllStatsUnlock();
        localStorage.removeItem('lastSalonRefresh');
        yield put(authActions.LOG_OUT.SUCCESS());
        callback && typeof callback === 'function' && callback();
      },
    (err) => {
      alert('error');
      authActions.LOG_OUT.FAILED(err);
    },
  );

const getActiveSignUpStep = ({ _, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        // const { data } = yield call(() => api.get('/business/auth/tokenLogin'));
        yield put(authActions.GET_ACTIVE_SIGN_UP_STEP.SUCCESS(1));
        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.GET_ACTIVE_SIGN_UP_STEP.FAILED(err),
  );

const setSalonWorkTimes = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        // yield call(() => api.post('/business/saloni/updateOrariSalone', {orari: payload}));
        const workTimes = yield select(salonWorkTimes());
        const req = {
          workTimes,
          ...payload,
        };

        yield put(authActions.SET_SALON_WORK_TIMES.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.SET_SALON_WORK_TIMES.FAILED(err),
  );

const setSalonServices = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        // yield call(() => api.post('/business/saloni/updateOrariSalone', {orari: payload}));
        yield put(authActions.SET_SALON_SERVICES.SUCCESS(payload));

        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.SET_SALON_SERVICES.FAILED(err),
  );

const setSalonWorkers = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        // yield call(() => api.post('/business/saloni/updateOrariSalone', {orari: payload}));
        yield put(authActions.SET_SALON_WORKERS.SUCCESS(payload));

        callback && typeof callback === 'function' && callback();
      },
    (err) => authActions.SET_SALON_WORKERS.FAILED(err),
  );

const createCustomer = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(async () => await api.post('/business/createCustomer', payload));
        yield put(authActions.CREATE_CUSTOMER.SUCCESS(data));
        if (data?.customer?.id) {
          yield put(authActions.SET_ID_COSTUMER_STRIPE.SUCCESS({ idCustomerStripe: data.customer.id }));
        }
        callback?.();
      },
    (err) => authActions.CREATE_CUSTOMER.FAILED(err),
  );

  const sendEmailRecovery = ({payload, callback}) =>
  sagaAssessor(
    () =>
      function* () {
        yield call(() => api.post('/business/auth/recoveryPwd', payload));
        callback?.();
      },
    (err) => authActions.SEND_EMAIL_RECOVERY.FAILED(err),
  );

  const resetPassword = ({payload, callback}) =>
  sagaAssessor(
    () =>
      function* () {
        yield call(() => api.post('/business/auth/resetPwd', payload, {headers: {Authorization: localStorage.getItem('tokenRecovery')}}));
        localStorage.removeItem('tokenRecovery');
        callback?.();
      },
    (err) => authActions.RESET_PASSWORD.FAILED(err),
  );



export default function* () {
  yield takeLatest(authTypes.SIGN_IN.REQUEST, signIn);
  yield takeLatest(authTypes.FETCH_SALON.REQUEST, fetchSalon);
  yield takeLatest(authTypes.SET_ACTIVE_SALON.REQUEST, setActiveSalon);
  yield takeLatest(authTypes.EDIT_SALON_INFO.REQUEST, editSalon);
  yield takeLatest(authTypes.RESET_HOME_STATS.REQUEST, resetHomeStats);
  yield takeLatest(authTypes.EDIT_SALON_AVATAR.REQUEST, editSalonAvatar);
  yield takeLatest(authTypes.EDIT_SALON_COVER.REQUEST, editSalonCover);
  yield takeLatest(authTypes.EDIT_SALON_PASSWORD.REQUEST, editSalonPassword);
  yield takeLatest(authTypes.DELETE_SALON.REQUEST, deleteSalon);
  yield takeLatest(authTypes.LOG_OUT.REQUEST, logOut);
  yield takeLatest(authTypes.GET_ACTIVE_SIGN_UP_STEP.REQUEST, getActiveSignUpStep);
  yield takeLatest(authTypes.SET_SALON_WORK_TIMES.REQUEST, setSalonWorkTimes);
  yield takeLatest(authTypes.SET_SALON_SERVICES.REQUEST, setSalonServices);
  yield takeLatest(authTypes.SET_SALON_WORKERS.REQUEST, setSalonWorkers);
  yield takeLatest(authTypes.ACTIVE_PAYMENT.REQUEST, activePayment);
  yield takeLatest(authTypes.CREATE_CUSTOMER.REQUEST, createCustomer);
  yield takeLatest(authTypes.SEND_EMAIL_RECOVERY.REQUEST, sendEmailRecovery);
  yield takeLatest(authTypes.RESET_PASSWORD.REQUEST, resetPassword);
}
